import ErrorIcon from '@atlaskit/icon/glyph/error';
import Select from '@atlaskit/select';
import { colors } from '@atlaskit/theme';

import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { State } from 'model/State';
import t from 'modules/i18n/intl';
import config from 'appconfig';
import {
  isQuoteMigrationInProgress,
  isOtherOrderErrorReason,
  isQuoteMigrationCompleted,
} from 'util/mac/helpers';
import { Dimensions } from 'components/visuals';
import RedirectToProductList from './components/redirectToProductList';
import MACPageHeader from './components/macPageHeader';
import {
  DescriptionLabel,
  ErrorBody,
  FormGroup,
  FormLabel,
  HeaderContainer,
  LabelWrapper,
  LinkButton,
  LookupErrorBanner,
  PageH3Header,
  PageTitle,
  RowContainer,
  StyledSpan,
  StyledTextfield,
  SubmitButton,
  TextfieldWrapper,
} from './components/styledComponents';
import { fetchCartByOrderNumber } from './duck/actions/cart';
import {
  getMACPayOnAccount,
  getMACUserChannel,
  selectCartOrderResponse,
  selectCurrency,
  selectVerificationHash,
} from './duck/selectors';
import { fetchVerificationHash } from './duck/actions/orders';
import { PartnerPortalLink } from '../../util/constants';

const ORDER_MISMATCH_ERROR = {
  id: 'mac.payment-page.error.does-not-match',
  defaultMessage:
    'The information provided does not match the order. Please reach out to us through our {contactUsLink} or have a look at our {faqLink}. If you are an Atlassian Partner, please check out our {ppLink}.',
  values: {
    contactUsLink: (
      <a href={config.companyContact}>
        <FormattedMessage
          defaultMessage="contact forms"
          id="mac.payment-page.error.does-not-match.contact-us"
        />
      </a>
    ),
    faqLink: (
      <a href={`${config.wacPurchaseLicensingUrl}#ordering`}>
        <FormattedMessage defaultMessage="FAQ" id="mac.payment-page.error.does-not-match.faq" />
      </a>
    ),
    ppLink: (
      <a href={PartnerPortalLink}>
        <FormattedMessage
          defaultMessage="Partner Portal"
          id="mac.payment-page.error.does-not-match.partner-portal"
        />
      </a>
    ),
  },
};

type ErrorMessageType = {
  id: string;
  defaultMessage: string;
  values?: any;
};

const alternativePaymentOptionsLink = (chunks) => (
  <a href={`${config.wacPurchaseLicensingUrl}/howtopay/`}>{chunks}</a>
);

const orderFormLink = (chunks) => <a href="/">{chunks}</a>;

const PaymentPage = () => {
  const dispatch = useDispatch();

  const emptyErrorMessage: ErrorMessageType = {
    id: '',
    defaultMessage: '',
  };
  const [errorMessage, setErrorMessage] = useState(emptyErrorMessage);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [currencyOptions, setCurrencyOptions] = useState<{ value: string; label: string }[]>([]);
  const [formData, setFormData] = useState({
    orderNumber: null,
    currency: { value: 'USD', label: 'USD' },
    total: null,
    email: null,
  });

  const isPayOnAccount = useSelector(getMACPayOnAccount);
  const cartOrderResponse = useSelector((state) =>
    selectCartOrderResponse(formData.orderNumber as unknown as number, state as State),
  );
  const verificationHash = useSelector((state) =>
    selectVerificationHash(formData.orderNumber as unknown as number, state as State),
  );
  const { isPartner } = useSelector((state: State) => getMACUserChannel(state));
  const currencyList = useSelector(selectCurrency);

  useEffect(() => {
    document.title = 'My Atlassian | Payment Page';
  }, [isPayOnAccount, cartOrderResponse, isPartner, currencyList]);

  useEffect(() => {
    if (currencyList.length) {
      setCurrencyOptions(currencyList.map((currency) => ({ label: currency, value: currency })));
    }
  }, [currencyList.length]);

  const handleNumericFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: +value,
    });
  };

  const handleEmailChange = (e) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      email: value,
    });
  };

  const handleSelectChange = (newValue) => {
    setFormData({
      ...formData,
      currency: newValue,
    });
  };

  useEffect(() => {
    if (!formData.orderNumber || !formData.total || !formData.currency.value || !formData.email) {
      setIsSubmitDisabled(true);
    } else {
      setIsSubmitDisabled(false);
    }
  }, [formData.orderNumber, formData.total, formData.currency.value, formData.email]);

  const handleSubmit = async () => {
    if (!formData.orderNumber || !formData.total || !formData.currency.value || !formData.email) {
      return;
    }
    dispatch(fetchVerificationHash(formData.orderNumber, +formData.total, formData.email));
  };

  useEffect(() => {
    if (verificationHash && formData.orderNumber) {
      dispatch(fetchCartByOrderNumber(formData.orderNumber, verificationHash));
    }
  }, [verificationHash]);

  useEffect(() => {
    if (cartOrderResponse?.isCompleted) {
      setErrorMessage(emptyErrorMessage);
      const cartForOrder = cartOrderResponse?.ifSuccess((r) => r);

      if (!cartForOrder) {
        setErrorMessage(ORDER_MISMATCH_ERROR);
        return;
      }

      if (isQuoteMigrationInProgress(cartForOrder.status, cartForOrder.ccpQuoteMigrationStatus)) {
        setErrorMessage({
          id: 'mac.payment-page.error.quote-migration',
          defaultMessage:
            'The Quote is migrating to another billing system. It can take 2-5 minutes. Any action with the quote is restricted.',
        });
        return;
      }

      if (isQuoteMigrationCompleted(cartForOrder.status, cartForOrder.ccpQuoteMigrationStatus)) {
        window.location.href = isPartner
          ? `${config.ppcUrl}/account/${cartForOrder.ccpQuoteTxaId}/quotes/${cartForOrder.ccpQuoteUuid}`
          : `${config.adminHubUrl}/billing/${cartForOrder.ccpQuoteTxaId}/quotes/${cartForOrder.ccpQuoteUuid}`;
        return;
      }

      if (
        isOtherOrderErrorReason(
          cartForOrder.status,
          cartForOrder.ccpQuoteMigrationStatus,
          cartForOrder.cartOrderDetails,
          formData,
        )
      ) {
        setErrorMessage(ORDER_MISMATCH_ERROR);
        return;
      }

      window.location.href = `/purchase/checkout/payment?uuid=${cartForOrder.uuid}`;
    }
  }, [cartOrderResponse]);

  return (
    <>
      <RedirectToProductList />
      <MACPageHeader />
      <HeaderContainer>
        <PageTitle>
          <FormattedMessage defaultMessage="Payment" id="mac.payment-page.header-1" />
        </PageTitle>
        <div data-cy="mac-payment-links">
          <LinkButton appearance="link" href={config.wacPurchaseLicensingUrl}>
            <FormattedMessage
              defaultMessage="Pricing and Licensing FAQ"
              id="mac.payment-page.links.pricing-and-licensing-faq"
            />
          </LinkButton>
          <LinkButton appearance="link" href={config.companyContact}>
            <FormattedMessage
              defaultMessage="Questions? Contact Us"
              id="mac.payment-page.links.contact-us"
            />
          </LinkButton>
        </div>
      </HeaderContainer>
      <p>
        <FormattedMessage
          defaultMessage="This secure payment form is for credit card payments on an existing open quote or invoice. We accept American Express, Visa, and MasterCard."
          id="mac.payment-page.content.p1"
        />
        {isPayOnAccount && (
          <>
            {' '}
            {t(
              {
                defaultMessage:
                  'You can also pay a quote on account under the terms outlined in your partner agreement. Learn more about <alternativePaymentOptionsLink>alternative payment options</alternativePaymentOptionsLink>. If you do not already have a quote or invoice, you can create one using the <orderFormLink>order form</orderFormLink>.',
                id: 'mac.payment-page.content.p2',
              },
              {
                alternativePaymentOptionsLink,
                orderFormLink,
              },
            )}
          </>
        )}
      </p>
      <PageH3Header>
        <FormattedMessage defaultMessage="Quote / Invoice Details" id="mac.payment-page.header-2" />
      </PageH3Header>
      {errorMessage.id && (
        <LookupErrorBanner>
          <ErrorBody data-cy="payment-error-message">
            <ErrorIcon primaryColor={colors.R400} label="payment-error-message-icon" />
            <StyledSpan $padding={`${Dimensions.units(2.25)}px ${Dimensions.units(1.25)}px`}>
              <FormattedMessage
                id={errorMessage.id}
                defaultMessage={errorMessage.defaultMessage}
                values={errorMessage.values}
              />
            </StyledSpan>
          </ErrorBody>
        </LookupErrorBanner>
      )}
      <FormGroup>
        <RowContainer>
          <LabelWrapper>
            <FormLabel htmlFor="orderNumber">
              <FormattedMessage
                id="mac.payment-page.input.order.label"
                defaultMessage="Order # (AT-)*"
              />
            </FormLabel>
          </LabelWrapper>
          <TextfieldWrapper>
            <StyledTextfield
              $height={3.75}
              data-cy="payment-order-id"
              id="orderNumber"
              value={formData.orderNumber || ''}
              name="orderNumber"
              onChange={handleNumericFieldChange}
              type="number"
            />
          </TextfieldWrapper>
        </RowContainer>
        <RowContainer>
          <LabelWrapper>
            <FormLabel htmlFor="orderCurrency">
              <FormattedMessage
                id="mac.payment-page.input.currency.label"
                defaultMessage="Invoiced Currency"
              />
              *
            </FormLabel>
          </LabelWrapper>
          <TextfieldWrapper>
            <Select
              inputId="orderCurrency"
              options={currencyOptions}
              value={formData.currency}
              onChange={handleSelectChange}
            />
            <DescriptionLabel htmlFor="orderCurrency">
              <FormattedMessage
                id="mac.payment-page.input.currency.hint"
                defaultMessage="The invoiced currency is noted in the 'Total Due' section of your quote / invoice."
              />
            </DescriptionLabel>
          </TextfieldWrapper>
        </RowContainer>
        <RowContainer>
          <LabelWrapper>
            <FormLabel htmlFor="orderTotal">
              <FormattedMessage id="mac.payment-page.input.total.label" defaultMessage="Confirm" />{' '}
              {formData.currency.value}*
            </FormLabel>
          </LabelWrapper>
          <TextfieldWrapper>
            <StyledTextfield
              $height={3.75}
              data-cy="payment-total"
              id="orderTotal"
              value={formData.total || ''}
              name="total"
              onChange={handleNumericFieldChange}
              type="number"
            />
          </TextfieldWrapper>
        </RowContainer>
        <RowContainer>
          <LabelWrapper>
            <FormLabel htmlFor="orderEmail">
              <FormattedMessage
                id="mac.payment-page.input.billing-email.label"
                defaultMessage="Email"
              />
              *
            </FormLabel>
          </LabelWrapper>
          <TextfieldWrapper>
            <StyledTextfield
              $height={3.75}
              data-cy="payment-email"
              id="orderEmail"
              value={formData.email || ''}
              name="email"
              onChange={handleEmailChange}
            />
            <DescriptionLabel htmlFor="orderEmail">
              <FormattedMessage
                id="mac.payment-page.input.billing-email.hint"
                defaultMessage="Billing contact's email on the Quote"
              />
            </DescriptionLabel>
          </TextfieldWrapper>
        </RowContainer>
        <div>
          <SubmitButton
            data-cy="payment-continue-button"
            appearance="primary"
            onClick={handleSubmit}
            type="submit"
            isDisabled={isSubmitDisabled}
          >
            <FormattedMessage defaultMessage="Continue" id="mac.payment-page.button.continue" />
          </SubmitButton>
        </div>
      </FormGroup>
    </>
  );
};

export default PaymentPage;
